type Slot =
    | { type: 'item'; el: HTMLElement }
    | {
        type: 'nested'
        parentEl: HTMLElement
        nestedEl: HTMLElement
        includeParent: boolean
        nestedChildren: HTMLElement[]
    }

const DURATION_MS = 800
const EASE = 'cubic-bezier(0.77, 0, 0.175, 1)'
const DEFAULT_STAGGER_MS = 100
const DEFAULT_DISTANCE = '2em'
const DEFAULT_START = 'top 80%'

const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

function setHidden(el: HTMLElement, distance: string) {
    el.style.transition = 'none'
    el.style.opacity = '0'
    el.style.transform = `translateY(${distance})`
    el.style.visibility = 'hidden'
    void el.offsetWidth
}

function reveal(el: HTMLElement, delayMs: number) {
    el.style.transition = `transform ${DURATION_MS}ms ${EASE} ${delayMs}ms, opacity ${DURATION_MS}ms ${EASE} ${delayMs}ms, visibility 0ms linear ${delayMs}ms`
    el.style.visibility = 'visible'
    el.style.opacity = '1'
    el.style.transform = 'translateY(0)'

    const onEnd = (e: TransitionEvent) => {
        if (e.target !== el || e.propertyName !== 'transform') return
        el.removeEventListener('transitionend', onEnd)
        el.style.transition = ''
        el.style.opacity = ''
        el.style.transform = ''
        el.style.visibility = ''
    }
    el.addEventListener('transitionend', onEnd)
}

function rootMarginFromStart(start: string): string {
    const match = start.match(/top\s+(\d+)%/)
    if (!match) return '0px 0px -20% 0px'
    const pct = 100 - parseInt(match[1], 10)
    return `0px 0px -${pct}% 0px`
}

function observeOnce(target: HTMLElement, rootMargin: string, callback: () => void) {
    const io = new IntersectionObserver(
        (entries, observer) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return
                callback()
                observer.unobserve(entry.target)
            })
        },
        { rootMargin, threshold: 0 }
    )
    io.observe(target)
}

document.querySelectorAll<HTMLElement>('[data-reveal-group]').forEach((groupEl) => {
    if (prefersReduced) return

    const groupStaggerMs = parseFloat(groupEl.getAttribute('data-stagger') || '') || DEFAULT_STAGGER_MS
    const groupDistance = groupEl.getAttribute('data-distance') || DEFAULT_DISTANCE
    const rootMargin = rootMarginFromStart(groupEl.getAttribute('data-start') || DEFAULT_START)

    const directChildren = Array.from(groupEl.children).filter(
        (el): el is HTMLElement => el.nodeType === 1
    )

    if (!directChildren.length) {
        setHidden(groupEl, groupDistance)
        observeOnce(groupEl, rootMargin, () => reveal(groupEl, 0))
        return
    }

    const slots: Slot[] = []
    directChildren.forEach((child) => {
        const nestedEl = child.matches('[data-reveal-group-nested]')
            ? child
            : child.querySelector<HTMLElement>(':scope [data-reveal-group-nested]')

        if (nestedEl) {
            const includeParent =
                child.getAttribute('data-ignore') !== 'true' &&
                (child.getAttribute('data-ignore') === 'false' ||
                    nestedEl.getAttribute('data-ignore') === 'false')

            const nestedChildren = Array.from(nestedEl.children).filter(
                (el): el is HTMLElement =>
                    el.nodeType === 1 && el.getAttribute('data-ignore') !== 'true'
            )

            slots.push({ type: 'nested', parentEl: child, nestedEl, includeParent, nestedChildren })
        } else {
            if (child.getAttribute('data-ignore') === 'true') return
            slots.push({ type: 'item', el: child })
        }
    })

    slots.forEach((slot) => {
        if (slot.type === 'item') {
            const isNestedSelf = slot.el.matches('[data-reveal-group-nested]')
            const d = isNestedSelf
                ? groupDistance
                : slot.el.getAttribute('data-distance') || groupDistance
            setHidden(slot.el, d)
        } else {
            if (slot.includeParent) setHidden(slot.parentEl, groupDistance)
            const nestedD = slot.nestedEl.getAttribute('data-distance') || groupDistance
            slot.nestedChildren.forEach((target) => setHidden(target, nestedD))
        }
    })

    observeOnce(groupEl, rootMargin, () => {
        slots.forEach((slot, slotIndex) => {
            const slotTime = slotIndex * groupStaggerMs

            if (slot.type === 'item') {
                reveal(slot.el, slotTime)
                return
            }

            if (slot.includeParent) reveal(slot.parentEl, slotTime)

            const nestedMs = parseFloat(slot.nestedEl.getAttribute('data-stagger') || '')
            const nestedStaggerMs = isNaN(nestedMs) ? groupStaggerMs : nestedMs
            slot.nestedChildren.forEach((nestedChild, nestedIndex) => {
                reveal(nestedChild, slotTime + nestedIndex * nestedStaggerMs)
            })
        })
    })
})
